import type { LoomExplicitTrimPreview, LoomExplicitTrimSurface } from "./loomExplicitTrimPreview";
import type { LoomNativeHybridLayers } from "./LoomNativeHybridViewer";
import type { LoomAnalyticPreviewSurface, LoomPickTarget } from "./loomNativeViewerHelpers";
import type { LoomProvisionalUnionPreview, LoomProvisionalUnionSurface } from "./loomProvisionalUnionPreview";

interface LoomNativeViewerEvidenceProps {
  analyticPreviewActive: boolean;
  analyticPreviewSurface?: LoomAnalyticPreviewSurface | null;
  authoringPreview: boolean;
  explicitTrimPreview: LoomExplicitTrimPreview | null;
  explicitTrimSurface: LoomExplicitTrimSurface | null;
  gpuUnionActive: boolean;
  gpuUnionOperandCount: number;
  jacobianParameterId: string;
  jacobianVectorCount: number;
  layers: LoomNativeHybridLayers;
  livePreviewOverlayActive: boolean;
  overlaysPresented: boolean;
  parameterLineSegmentCount: number;
  pickTargets: readonly LoomPickTarget[];
  provisionalUnionPreview: LoomProvisionalUnionPreview | null;
  provisionalUnionSurface: LoomProvisionalUnionSurface | null;
  presentationUnavailableReason: string | null;
  renderError: string | null;
  sourceFixtureOverlayAvailable: boolean;
  trailingEdgeSegmentCount: number;
}

export function LoomNativeViewerEvidence(props: LoomNativeViewerEvidenceProps) {
  const {
    analyticPreviewActive, analyticPreviewSurface, authoringPreview,
    explicitTrimPreview, explicitTrimSurface, jacobianParameterId, jacobianVectorCount,
    gpuUnionActive, gpuUnionOperandCount, layers, livePreviewOverlayActive, overlaysPresented, parameterLineSegmentCount, pickTargets,
    presentationUnavailableReason, provisionalUnionPreview, provisionalUnionSurface, renderError,
    sourceFixtureOverlayAvailable, trailingEdgeSegmentCount,
  } = props;
  return <>
    {gpuUnionActive && layers.surface ? <div className="pg-render-evidence" data-testid="loom-gpu-implicit-union">
      {gpuUnionOperandCount} authored explicit operands · GPU implicit fragment classification · visual-only · no constructed seam, topology, or panel receipt
    </div> : null}
    {!gpuUnionActive && explicitTrimSurface && layers.surface ? <div className="pg-render-evidence" data-testid="loom-symmetric-explicit-trim">
      {(explicitTrimSurface.faces.length / 3).toLocaleString()} symmetric explicit-trim triangles
    </div> : null}
    {!gpuUnionActive && provisionalUnionSurface && layers.surface ? <div className="pg-render-evidence" data-testid="loom-provisional-explicit-union">
      {(provisionalUnionSurface.faces.length / 3).toLocaleString()} retained explicit triangles · {provisionalUnionSurface.sourceOperandIds.length} source operands · {provisionalUnionSurface.boundaryEdgeCount} unshared boundary edges
    </div> : null}
    {!gpuUnionActive && explicitTrimSurface && layers.sharedSeam && overlaysPresented ? <div className="pg-render-evidence is-seam" data-testid="loom-explicit-trim-seams">
      {explicitTrimSurface.seamRings.length} shared seam rings · {explicitTrimSurface.seamRings.reduce((sum, ring) => sum + ring.length, 0)} vertices
    </div> : null}
    {!gpuUnionActive && explicitTrimSurface && layers.parameterization && overlaysPresented ? <div className="pg-render-evidence is-parameter" data-testid="loom-native-parameterization">
      {parameterLineSegmentCount.toLocaleString()} structured u/v segments
    </div> : null}
    {!gpuUnionActive && explicitTrimSurface && layers.tessellation ? <span data-testid="loom-explicit-trim-tessellation" hidden>{explicitTrimSurface.faces.length / 3}</span> : null}
    {sourceFixtureOverlayAvailable && layers.trailingEdge && overlaysPresented && trailingEdgeSegmentCount > 0 ? <div className="pg-render-evidence is-te" data-testid="loom-symmetric-smart-trailing-edge">
      {trailingEdgeSegmentCount} reflected source-fixture TE edges · geometry-only overlay
    </div> : null}
    {sourceFixtureOverlayAvailable && layers.jacobian && overlaysPresented && jacobianVectorCount > 0 ? <div className="pg-render-evidence is-parameter" data-testid="loom-symmetric-jacobian-vectors">
      {jacobianVectorCount} reflected source-fixture dV/d{jacobianParameterId} vectors
    </div> : null}
    {authoringPreview ? <div className="pg-render-evidence is-authoring" data-testid="loom-authoring-preview">
      {livePreviewOverlayActive
        ? "Amber provisional candidate over ghosted last accepted aircraft · visual only · exact rebuild pending"
        : analyticPreviewActive
        ? `live analytic dV/d${analyticPreviewSurface?.parameterId} · symmetric frozen-topology explicit-trim linearization`
        : gpuUnionActive ? "GPU implicit fragment classification of authored explicit operands · visual-only · exact rebuild stale"
          : explicitTrimSurface ? "symmetric explicit-trim linearization · exact hybrid extraction stale"
          : provisionalUnionSurface ? "provisional explicit-patch union · implicit classification only · exact hybrid extraction stale"
            : "explicit trim unavailable · showing explicit operands · exact hybrid extraction stale"}
    </div> : null}
    {analyticPreviewActive && explicitTrimSurface ? <span data-testid="loom-analytic-preview-surface" hidden>{explicitTrimSurface.faces.length / 3}</span> : null}
    {!explicitTrimSurface ? <span data-testid="loom-trim-unavailable" hidden>{explicitTrimPreview?.kind === "unavailable" ? explicitTrimPreview.reason : "asset unavailable"}</span> : null}
    {!provisionalUnionSurface ? <span data-testid="loom-provisional-union-unavailable" hidden>{provisionalUnionPreview?.kind === "unavailable" ? provisionalUnionPreview.reason : "not requested"}</span> : null}
    {pickTargets.map((target) => <span
      data-canvas-x={target.x.toFixed(2)} data-canvas-y={target.y.toFixed(2)}
      data-world-y-max={target.worldYMax.toFixed(5)} data-world-y-min={target.worldYMin.toFixed(5)}
      data-testid={`loom-pick-target-${target.nodeId}`} hidden key={target.nodeId}
    />)}
    {renderError ? <div className="pg-render-error" role="alert">{renderError}</div> : null}
    {presentationUnavailableReason ? <div className="pg-render-error" data-testid="loom-presentation-unavailable" role="status">{presentationUnavailableReason}</div> : null}
  </>;
}
